import { Link, router } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { OrganizationLayout } from '@/layouts/organization-layout'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { getDateLocale } from '@/lib/i18n'

interface Project {
  id: number
  name: string
  slug: string
  is_archived: boolean
  created_at: string
  updated_at: string
}

interface Organization {
  id: number
  name: string
}

interface Counts {
  all: number
  active: number
  archived: number
}

interface OrganizationProjectsIndexProps {
  organization: Organization
  projects: Project[]
  current_filter: string
  counts: Counts
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
      organization_role: string
    }
  }
}

function Index({ projects, current_filter, counts }: OrganizationProjectsIndexProps) {
  const { t } = useTranslation('organizations')

  const handleFilterChange = (filter: string) => {
    const params: Record<string, string> = {}
    if (filter !== 'all') {
      params.filter = filter
    }
    router.get('/organization/projects', params, { preserveState: true })
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col">
        <div className="px-4 py-6 lg:px-6">
          <div className="mx-auto max-w-4xl space-y-8">

            {/* Header */}
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
                {t('projects.index.title')}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('projects.index.subtitle', { count: counts.all })}
              </p>
            </div>

            {/* Filter Tabs */}
            <Tabs value={current_filter} onValueChange={handleFilterChange}>
              <TabsList>
                <TabsTrigger value="all">
                  {t('projects.index.tabs.all', { count: counts.all })}
                </TabsTrigger>
                <TabsTrigger value="active">
                  {t('projects.index.tabs.active', { count: counts.active })}
                </TabsTrigger>
                <TabsTrigger value="archived">
                  {t('projects.index.tabs.archived', { count: counts.archived })}
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Projects Table */}
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {t('projects.index.table.name')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {t('projects.index.table.status')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {t('projects.index.table.created')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {t('projects.index.table.updated')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {t('projects.index.table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {projects.length > 0 ? (
                    projects.map((project) => (
                      <tr key={project.id} className="hover:bg-muted/50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm font-medium text-foreground">{project.name}</div>
                          {project.slug && (
                            <div className="text-xs text-muted-foreground">{project.slug}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {project.is_archived ? (
                            <Badge variant="warning">{t('projects.status.archived')}</Badge>
                          ) : (
                            <Badge variant="success">{t('projects.status.active')}</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {new Date(project.created_at).toLocaleDateString(getDateLocale(), {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {new Date(project.updated_at).toLocaleDateString(getDateLocale(), {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                          <Link
                            href={`/organization/projects/${project.id}`}
                            className="text-primary hover:text-primary/80"
                          >
                            {t('projects.index.view')}
                          </Link>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-muted-foreground">
                        {t('projects.index.empty')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

          </div>
        </div>
      </div>
    </div>
  )
}

Index.layout = (page: React.ReactNode) => <OrganizationLayout>{page}</OrganizationLayout>

export default Index
